import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import imageCompression from "browser-image-compression";
import { getApiBaseUrl } from "../config/api";
import RichTextEditor from "./shared/RichTextEditor";
import CaptionAssist from "./shared/CaptionAssist";
import FitCoverImage from "./shared/FitCoverImage";
import { isEmployer, canManagePost } from "../utils/userRole";

const API_URL = getApiBaseUrl();

const inputStyle = {
  width: "100%",
  padding: "10px 12px",
  fontSize: 14,
  borderRadius: 12,
  border: "1px solid rgba(148, 163, 184, 0.9)",
  marginBottom: 10,
  outline: "none",
  backgroundColor: "rgba(248, 250, 252, 0.95)",
  color: "#0f172a",
  boxSizing: "border-box",
};

function resolveCoverPreview(cover) {
  if (!cover) return null;
  const raw = String(cover).trim();
  if (!raw) return null;
  if (/^(https?:|blob:|data:)/i.test(raw)) return raw;
  const base = API_URL.replace(/\/+$/, "");
  const path = raw.replace(/^\/+/, "");
  return `${base}/${path}`;
}

/**
 * Employer hiring / job ad composer (article-style).
 */
export default function HiringForm({
  user,
  onClose,
  onSuccess,
  onRefresh,
  groupId = null,
  post = null,
}) {
  const isEdit = Boolean(post?.id);
  const existingDeadline = String(post?.hiring_deadline || "").trim().slice(0, 10);
  const [title, setTitle] = useState(post?.title || "");
  const [company, setCompany] = useState(post?.hiring_company || "");
  const [location, setLocation] = useState(post?.hiring_location || "");
  const [applyUrl, setApplyUrl] = useState(post?.hiring_apply_url || "");
  const [useDeadline, setUseDeadline] = useState(Boolean(existingDeadline));
  const [deadline, setDeadline] = useState(existingDeadline);
  const [content, setContent] = useState(
    post?.hiring_description || post?.text || ""
  );
  const [coverFile, setCoverFile] = useState(null);
  const [coverPreview, setCoverPreview] = useState(
    resolveCoverPreview(post?.cover_image)
  );
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("");
  const previewRef = useRef(null);

  useEffect(() => {
    return () => {
      if (previewRef.current) URL.revokeObjectURL(previewRef.current);
    };
  }, []);

  if (!isEmployer(user) && !(isEdit && canManagePost(user, post))) {
    return null;
  }

  const handleCoverChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      alert("Cover must be an image.");
      return;
    }
    if (previewRef.current) URL.revokeObjectURL(previewRef.current);
    const url = URL.createObjectURL(file);
    previewRef.current = url;
    setCoverFile(file);
    setCoverPreview(url);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user?.email && !user?.username) {
      alert("You must be logged in");
      return;
    }
    const plain = content.replace(/<[^>]+>/g, "").replace(/&nbsp;/gi, " ").trim();
    if (!title.trim() || !plain) {
      alert("Please add a job title and description.");
      return;
    }

    setLoading(true);
    setStatus(isEdit ? "Saving hiring post..." : "Publishing hiring post...");
    try {
      const formData = new FormData();
      user.email && formData.append("email", user.email);
      user.username && formData.append("username", user.username);
      user.country && formData.append("country", user.country);
      formData.append("title", title.trim());
      formData.append("content", content);
      formData.append("category", "Hiring");
      formData.append("post_type", "hiring");
      formData.append("company", company.trim());
      formData.append("location", location.trim());
      formData.append("apply_url", applyUrl.trim());
      formData.append(
        "deadline",
        useDeadline && deadline.trim() ? deadline.trim() : ""
      );
      if (user?.id) formData.append("user_id", user.id);
      if (isEdit) formData.append("post_id", post.id);
      if (groupId) {
        formData.append("group_id", groupId);
        if (!isEdit) formData.append("user_id", user.id);
      }

      if (coverFile) {
        setStatus("Compressing cover...");
        try {
          const compressed = await imageCompression(coverFile, {
            maxSizeMB: 1.5,
            maxWidthOrHeight: 1920,
            useWebWorker: true,
          });
          formData.append("cover", compressed, compressed.name);
        } catch {
          formData.append("cover", coverFile);
        }
      }

      setStatus("Uploading...");
      const endpoint = isEdit
        ? groupId
          ? `${API_URL}/update_group_post.php`
          : `${API_URL}/update_post.php`
        : groupId
          ? `${API_URL}/create_group_post.php`
          : `${API_URL}/create_post.php`;
      const { data } = await axios.post(endpoint, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        maxContentLength: Infinity,
        maxBodyLength: Infinity,
      });

      if (data.success) {
        onSuccess?.(data.post);
        onRefresh?.();
        onClose?.();
      } else {
        alert(data.message || (isEdit ? "Failed to update hiring post" : "Failed to publish hiring post"));
      }
    } catch (err) {
      console.error(err);
      alert("Upload error: " + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
      setStatus("");
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15,23,42,0.55)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 9999,
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#fff",
          borderRadius: 16,
          padding: 24,
          width: "100%",
          maxWidth: 720,
          maxHeight: "92vh",
          overflowY: "auto",
          position: "relative",
          boxShadow: "0 25px 50px rgba(15,23,42,0.35)",
        }}
      >
        <button
          type="button"
          onClick={onClose}
          style={{
            position: "absolute",
            top: 12,
            right: 14,
            border: "none",
            background: "transparent",
            fontSize: 22,
            cursor: "pointer",
            color: "#64748b",
          }}
        >
          ×
        </button>

        <h2 style={{ margin: "0 0 6px", fontSize: 22, color: "#0f172a" }}>
          {isEdit ? "Edit hiring post" : "Post a hiring ad"}
        </h2>
        <p style={{ margin: "0 0 16px", fontSize: 13, color: "#64748b" }}>
          {isEdit
            ? "Update the job details. Existing applications stay attached to this post."
            : "Visible to interns and everyone in the hiring feed."}
        </p>

        <form onSubmit={handleSubmit}>
          <input
            style={inputStyle}
            placeholder="Job title *"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
          />
          <input
            style={inputStyle}
            placeholder="Company"
            value={company}
            onChange={(e) => setCompany(e.target.value)}
          />
          <input
            style={inputStyle}
            placeholder="Location (e.g. Remote, Singapore)"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
          />
          <input
            style={inputStyle}
            type="url"
            placeholder="Apply link (https://...)"
            value={applyUrl}
            onChange={(e) => setApplyUrl(e.target.value)}
          />
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              fontSize: 13,
              color: "#334155",
              marginBottom: useDeadline ? 8 : 12,
              cursor: "pointer",
              userSelect: "none",
            }}
          >
            <input
              type="checkbox"
              checked={useDeadline}
              onChange={(e) => {
                const on = e.target.checked;
                setUseDeadline(on);
                if (!on) setDeadline("");
              }}
            />
            Set an application deadline (optional)
          </label>
          {useDeadline ? (
            <input
              style={inputStyle}
              type="date"
              value={deadline}
              onChange={(e) => setDeadline(e.target.value)}
              title="Application deadline"
              required
            />
          ) : (
            <p style={{ margin: "0 0 12px", fontSize: 12, color: "#64748b" }}>
              No deadline — listing stays open until you mark it closed.
            </p>
          )}

          <label style={{ display: "block", fontSize: 13, marginBottom: 6, color: "#475569" }}>
            Cover image (optional)
          </label>
          <input type="file" accept="image/*" onChange={handleCoverChange} style={{ marginBottom: 10 }} />
          <FitCoverImage src={coverPreview} maxHeight={220} radius={10} marginBottom={12} />

          <label style={{ display: "block", fontSize: 13, marginBottom: 6, color: "#475569" }}>
            Job description *
          </label>
          <RichTextEditor content={content} onChange={setContent} />
          <CaptionAssist
            kind="hiring"
            format="html"
            text={content}
            title={title}
            context={[company && `Company: ${company}`, location && `Location: ${location}`]
              .filter(Boolean)
              .join("\n")}
            userId={user?.id || user?.user_id}
            disabled={loading}
            style={{ marginTop: 8 }}
            onApply={setContent}
            onApplyTitle={(nextTitle) => {
              if (!title.trim() && nextTitle) setTitle(nextTitle);
            }}
          />

          {status && (
            <p style={{ margin: "10px 0 0", fontSize: 13, color: "#0369a1" }}>{status}</p>
          )}

          <button
            type="submit"
            disabled={loading}
            style={{
              marginTop: 16,
              width: "100%",
              padding: "12px 16px",
              borderRadius: 10,
              border: "none",
              background: loading ? "#94a3b8" : "#0f766e",
              color: "#fff",
              fontWeight: 650,
              fontSize: 15,
              cursor: loading ? "default" : "pointer",
            }}
          >
            {loading
              ? isEdit
                ? "Saving…"
                : "Publishing…"
              : isEdit
                ? "Save hiring post"
                : "Publish hiring post"}
          </button>
        </form>
      </div>
    </div>
  );
}
